import { computeRetryAt } from "./retryPolicy";

export const RECORDING_PROGRESS_PATH = "/public/recording/progress";
export const RECORDING_LEAVING_HINT_PATH = "/public/recording/leaving-hint";
// LSP-22802: deliberately not prefixed "leaving-hint": tests and journeys match hints by that path prefix.
export const RECORDING_LEAVING_HINT_RETRACT_PATH = "/public/recording/retract-leaving-hint";
export const RECORDING_PRESIGNED_URL_SAFETY_MARGIN_MS = 60_000;

export type FinalCountSource = "confirmed" | "recovered" | "snapshot" | "absent";
export interface RecordingWireFetch {
	fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response>;
}

export interface RecordingWireClientOptions {
	baseUrl?: string;
	fetcher?: RecordingWireFetch;
}

function requireSafeNonNegativeInteger(value: number, name: string): void {
	if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${name} must be a safe non-negative integer`);
}

function resolveProducedSoFar(maxProducedChunkIndex: number | undefined, producedSoFar: number | undefined): number | undefined {
	if (maxProducedChunkIndex !== undefined) requireSafeNonNegativeInteger(maxProducedChunkIndex, "maxProducedChunkIndex");
	if (producedSoFar !== undefined) requireSafeNonNegativeInteger(producedSoFar, "producedSoFar");
	if (maxProducedChunkIndex !== undefined && producedSoFar !== undefined && maxProducedChunkIndex !== producedSoFar) {
		throw new Error("producedSoFar must agree with maxProducedChunkIndex when both are supplied");
	}
	return producedSoFar ?? maxProducedChunkIndex;
}

export function isPresignedUrlFresh(url: string, now = Date.now(), safetyMarginMs = RECORDING_PRESIGNED_URL_SAFETY_MARGIN_MS): boolean {
	try {
		const parsed = new URL(url);
		const expiresText = parsed.searchParams.get("X-Amz-Expires");
		// Test/dev and non-SigV4 targets have no expiry envelope. They are usable until the
		// transport reports otherwise; only a complete SigV4 envelope can be pre-expired.
		if (expiresText === null) return true;
		const expires = Number(expiresText);
		if (!Number.isFinite(expires)) return true;
		const dateText = parsed.searchParams.get("X-Amz-Date") ?? "";
		const year = Number(dateText.slice(0, 4));
		const month = Number(dateText.slice(4, 6));
		const day = Number(dateText.slice(6, 8));
		const hour = Number(dateText.slice(9, 11));
		const minute = Number(dateText.slice(11, 13));
		const second = Number(dateText.slice(13, 15));
		const createdAt = Date.UTC(year, month - 1, day, hour, minute, second);
		return Number.isFinite(createdAt) && createdAt + expires * 1000 - safetyMarginMs > now;
	} catch {
		return false;
	}
}

export function recordingProgressBeaconUrl(input: {
	baseUrl?: string;
	sessionClientId: string;
	recordingClientId?: string;
	maxProducedChunkIndex: number;
	producedSoFar?: number;
}): string | undefined {
	if (!Number.isSafeInteger(input.maxProducedChunkIndex) || input.maxProducedChunkIndex <= 0) return undefined;
	const producedSoFar = resolveProducedSoFar(input.maxProducedChunkIndex, input.producedSoFar);
	const query = new URLSearchParams({
		sessionClientId: input.sessionClientId,
		maxProducedChunkIndex: String(input.maxProducedChunkIndex),
		producedSoFar: String(producedSoFar)
	});
	if (input.recordingClientId) query.set("recordingClientId", input.recordingClientId);
	return `${input.baseUrl ?? ""}${RECORDING_PROGRESS_PATH}?${query.toString()}`;
}

/**
 * LSP-18018: the "the client is going away" hint URL. Like the progress beacon it carries its only
 * parameter in the query string, because `navigator.sendBeacon(url)` sends no body and cannot set a
 * header — see the route comment in `apps/contact-api/src/routes/public.ts`.
 *
 * Returns undefined for a missing/blank conversationId so a caller can never beacon
 * `?conversationId=`, which the route's UUID pre-validation would reject anyway.
 */
export function recordingLeavingHintUrl(input: {
	baseUrl?: string;
	conversationId: string;
	source?: "pagehide" | "visibilitychange";
}): string | undefined {
	if (!input.conversationId) return undefined;
	const query = new URLSearchParams({ conversationId: input.conversationId });
	if (input.source) query.set("source", input.source);
	return `${input.baseUrl ?? ""}${RECORDING_LEAVING_HINT_PATH}?${query.toString()}`;
}

/**
 * LSP-22802: the "the customer came back" retraction URL for a visibilitychange leaving-hint. A separate
 * path, not a new `source` value: an older contact-api maps an unrecognized source to the pagehide window,
 * so it would shorten the very timer this asks it to restore, while an unknown path is a harmless 404.
 */
export function recordingLeavingHintRetractUrl(input: { baseUrl?: string; conversationId: string }): string | undefined {
	if (!input.conversationId) return undefined;
	const query = new URLSearchParams({ conversationId: input.conversationId });
	return `${input.baseUrl ?? ""}${RECORDING_LEAVING_HINT_RETRACT_PATH}?${query.toString()}`;
}

export class RecordingWireClient {
	private readonly fetcher: RecordingWireFetch;
	private readonly baseUrl: string;

	constructor(options: RecordingWireClientOptions = {}) {
		this.fetcher =
			options.fetcher ??
			(typeof fetch === "function"
				? // BOUND, not a bare `{ fetch }`. `this.fetcher.fetch(...)` invokes it as a method of the
					// fetcher object, so an unbound reference reaches the browser with `this === fetcher` and
					// Chrome refuses it outright: `TypeError: Failed to execute 'fetch' on 'Window': Illegal
					// invocation` — thrown before any request leaves, so it does not even appear in DevTools'
					// network panel. `UploadsFacadeClient` has always bound for this reason; this client did
					// not, and the gap stayed latent because its only in-browser callers were `progressBeacon`
					// and `leavingHint`, which use `navigator.sendBeacon` directly rather than this fetcher.
					// LSP-18271's `reportProgress` is the first real browser caller and it failed 100% of the
					// time. Unit tests cannot catch this: they inject a fetcher or stub `globalThis.fetch` with
					// a plain function, and a plain function has no `this` requirement to violate.
					{ fetch: fetch.bind(globalThis) }
				: (() => {
						throw new Error("fetch is unavailable");
					})());
		this.baseUrl = options.baseUrl ?? "";
	}

	/**
	 * LSP-18018: fire-and-forget hint that this client is leaving mid-recording, letting the server
	 * shortcut its passive wait-for-more-uploads cadence. Report-only — it never finalizes anything
	 * (contact-api's `scheduleFastFinalizeCheck` absorbs every no-op case), so a dropped beacon only
	 * costs latency. Returns sendBeacon's own boolean so a refused send stays observable.
	 */
	leavingHint(input: { conversationId: string; source?: "pagehide" | "visibilitychange" }): boolean {
		const url = recordingLeavingHintUrl({ ...input, baseUrl: this.baseUrl });
		if (!url || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;
		return navigator.sendBeacon(url);
	}

	progressBeacon(input: Parameters<typeof recordingProgressBeaconUrl>[0]): boolean {
		const url = recordingProgressBeaconUrl({ ...input, baseUrl: this.baseUrl });
		if (!url || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;
		return navigator.sendBeacon(url);
	}

	/**
	 * LSP-22802: fire-and-forget "the page is visible again" retraction of this page's visibilitychange
	 * leaving-hint (contact-api's `retractVisibilityLeavingHint` absorbs every no-op case). A dropped beacon
	 * leaves the short sticky wait in place, which is the pre-LSP-22802 behavior.
	 */
	leavingHintRetract(input: { conversationId: string }): boolean {
		const url = recordingLeavingHintRetractUrl({ ...input, baseUrl: this.baseUrl });
		if (!url || typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return false;
		return navigator.sendBeacon(url);
	}

	/**
	 * LSP-18271: the same progress report as `progressBeacon` above, but AWAITED — for a caller
	 * reporting mid-recording rather than at teardown.
	 *
	 * `sendBeacon` is the right transport at teardown and the wrong one here. It returns only whether
	 * the browser queued the request, so a caller whose durable cursor may advance only on a real
	 * server ack cannot use it: an unacked mark that is recorded as delivered is the exact failure
	 * mode that re-publishes a silently short recording. Mid-recording the page is alive, so a normal
	 * POST is available and its status IS the ack.
	 *
	 * `keepalive` because the scenario this serves is an OS kill of a backgrounded tab: a checkpoint
	 * dispatched moments before the kill still reaches the server, which is the whole point of
	 * reporting on a cadence that does not depend on teardown.
	 *
	 * Throws on a non-2xx so a failed report can never be mistaken for a delivered one. Returns
	 * undefined for a mark the URL builder refuses (`0`/non-safe — the server's "unknown" sentinel),
	 * so a caller can tell "nothing to report" from "reported".
	 */
	async reportProgress(input: Parameters<typeof recordingProgressBeaconUrl>[0]): Promise<Response | undefined> {
		const url = recordingProgressBeaconUrl({ ...input, baseUrl: this.baseUrl });
		if (!url) return undefined;
		const response = await this.fetcher.fetch(url, {
			method: "POST",
			keepalive: true
		});
		if (!response.ok)
			throw Object.assign(new Error(`Recording progress report failed: HTTP ${response.status}`), { status: response.status });
		return response;
	}
}

export function cappedRetryAt(attempt: number, now: number, random?: () => number): number | null {
	return computeRetryAt({ attempt, nowMs: now, random });
}
